import { ColumnDef } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';
import { SuperAdminBookingListItemDto } from '@/features/superAdmin/features/data/types/superAdmin-data.types';
import {
    formatDateOnly,
    formatDateTime,
    formatNavValue,
} from '@/features/superAdmin/features/data/shared/utils/superAdminDataFormatters';
import {
    getBookingStatusLabel,
    getDocumentTypeLabel,
    getEventTypeLabel,
} from '@/features/superAdmin/features/data/bookings/utils/superAdminBookingEnumLabels';

export const useSuperAdminBookingsPageColumns = () => {
    const { t } = useTranslation();

    const columns: ColumnDef<SuperAdminBookingListItemDto>[] = [
        { accessorKey: 'id', header: t('superAdmin.data.bookings.columns.id') },
        {
            accessorKey: 'documentNumber',
            header: t('superAdmin.data.bookings.columns.number'),
        },
        {
            accessorKey: 'documentDate',
            header: t('superAdmin.data.bookings.columns.date'),
            cell: ({ row }) => formatDateTime(row.original.documentDate),
        },
        {
            accessorKey: 'tenantName',
            header: t('superAdmin.data.bookings.columns.tenant'),
            cell: ({ row }) => formatNavValue(row.original.tenantId, row.original.tenantName),
        },
        {
            accessorKey: 'hotelName',
            header: t('superAdmin.data.bookings.columns.hotel'),
            cell: ({ row }) => formatNavValue(row.original.hotelId, row.original.hotelName),
        },
        {
            accessorKey: 'apartmentName',
            header: t('superAdmin.data.bookings.columns.room'),
            cell: ({ row }) => formatNavValue(row.original.apartmentId, row.original.apartmentName),
        },
        {
            accessorKey: 'ownerName',
            header: t('superAdmin.data.bookings.columns.owner'),
            cell: ({ row }) => formatNavValue(row.original.ownerId, row.original.ownerName),
        },
        {
            accessorKey: 'eventType',
            header: t('superAdmin.data.bookings.columns.event'),
            cell: ({ row }) => getEventTypeLabel(row.original.eventType, t),
        },
        {
            accessorKey: 'documentType',
            header: t('superAdmin.data.bookings.columns.type'),
            cell: ({ row }) => getDocumentTypeLabel(row.original.documentType, t),
        },
        {
            accessorKey: 'bookingStatus',
            header: t('superAdmin.data.bookings.columns.status'),
            cell: ({ row }) => getBookingStatusLabel(row.original.bookingStatus, t),
        },
        {
            accessorKey: 'checkInDate',
            header: t('superAdmin.data.bookings.columns.checkIn'),
            cell: ({ row }) => formatDateOnly(row.original.checkInDate),
        },
        {
            accessorKey: 'checkOutDate',
            header: t('superAdmin.data.bookings.columns.checkOut'),
            cell: ({ row }) => formatDateOnly(row.original.checkOutDate),
        },
        {
            accessorKey: 'guestFullName',
            header: t('superAdmin.data.bookings.columns.guest'),
        },
        {
            accessorKey: 'totalAmount',
            header: t('superAdmin.data.bookings.columns.amount'),
        },
        {
            accessorKey: 'externalId',
            header: t('superAdmin.data.bookings.columns.external'),
        },
    ];

    return columns;
};

export const saleTransactionNestedColumns: ColumnDef<SuperAdminBookingListItemDto['saleTransactions'][number]>[] = [
    { accessorKey: 'id', header: 'Id' },
    { accessorKey: 'serviceName', header: 'Service', cell: ({ row }) => formatNavValue(row.original.serviceId, row.original.serviceName) },
    { accessorKey: 'ownerName', header: 'Owner', cell: ({ row }) => formatNavValue(row.original.ownerId, row.original.ownerName) },
    { accessorKey: 'apartmentName', header: 'Apartment', cell: ({ row }) => formatNavValue(row.original.apartmentId, row.original.apartmentName) },
    { accessorKey: 'financeDocumentNumberNav', header: 'FinanceDocument', cell: ({ row }) => formatNavValue(row.original.financeDocumentId, row.original.financeDocumentNumberNav) },
    { accessorKey: 'accountingDate', header: 'AccountingDate' },
    { accessorKey: 'sales', header: 'Sales' },
    { accessorKey: 'roomRevenue', header: 'RoomRevenue' },
    { accessorKey: 'roomsRented', header: 'RoomsRented' },
];

export const saleForecastTransactionNestedColumns: ColumnDef<SuperAdminBookingListItemDto['saleForecastTransactions'][number]>[] = [
    { accessorKey: 'id', header: 'Id' },
    { accessorKey: 'serviceName', header: 'Service', cell: ({ row }) => formatNavValue(row.original.serviceId, row.original.serviceName) },
    { accessorKey: 'ownerName', header: 'Owner', cell: ({ row }) => formatNavValue(row.original.ownerId, row.original.ownerName) },
    { accessorKey: 'apartmentName', header: 'Apartment', cell: ({ row }) => formatNavValue(row.original.apartmentId, row.original.apartmentName) },
    { accessorKey: 'accountingDate', header: 'AccountingDate' },
    { accessorKey: 'sales', header: 'Sales' },
    { accessorKey: 'roomRevenue', header: 'RoomRevenue' },
    { accessorKey: 'roomsRented', header: 'RoomsRented' },
];
